export function normalizeText(value) {
  return String(value ?? '').trim()
}

export function formatDate(value) {
  const text = normalizeText(value)
  if (!text) return '-'

  const normalized = text.includes('T') ? text : text.replace(' ', 'T')
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) {
    return text
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function formatViewCount(value) {
  const numeric = Number(value || 0)
  if (!Number.isFinite(numeric) || numeric <= 0) return '-'
  if (numeric < 1000) return String(Math.round(numeric))

  const formatted = (numeric / 1000).toFixed(1)
  return `${formatted.replace(/\.0$/, '')}K`
}

export function resolveVideoCover(record) {
  const coverUrl = normalizeText(record?.coverUrl || record?.coverSourceUrl)
  if (coverUrl) return coverUrl

  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">
      <defs>
        <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#fff4f6" />
          <stop offset="100%" stop-color="#fee2e2" />
        </linearGradient>
      </defs>
      <rect width="640" height="360" rx="28" fill="url(#g)" />
      <circle cx="160" cy="180" r="78" fill="#e11d48" opacity="0.10" />
      <polygon points="142,140 142,220 208,180" fill="#e11d48" />
      <text x="250" y="172" font-size="28" font-family="Arial, PingFang SC, Microsoft YaHei, sans-serif" fill="#0f172a">
        YouTube
      </text>
      <text x="250" y="210" font-size="18" font-family="Arial, PingFang SC, Microsoft YaHei, sans-serif" fill="#64748b">
        ${escapeSvgText(record?.title || record?.videoId || 'Video')}
      </text>
    </svg>
  `.trim())
}

export function getZhSubtitle(record) {
  const subtitles = Array.isArray(record?.subtitles) ? record.subtitles : []
  const target = subtitles.find((item) => String(item?.langCode || '') === 'zh-CN')
  return normalizeText(target?.content)
}

export function normalizeSelectionText(value) {
  return String(value || '').replace(/\r\n?/g, '\n')
}

export function buildBreakdownSegments(sourceText, breakdowns, keyPrefix = 'breakdown') {
  const text = normalizeSelectionText(sourceText)
  if (!text) return []

  const segments = []
  let cursor = 0
  const sortedBreakdowns = Array.isArray(breakdowns)
    ? [...breakdowns].sort((left, right) => Number(left?.sourceStartOffset || 0) - Number(right?.sourceStartOffset || 0))
    : []

  for (const breakdown of sortedBreakdowns) {
    const start = Number.isFinite(Number(breakdown?.sourceStartOffset)) ? Number(breakdown.sourceStartOffset) : -1
    const end = Number.isFinite(Number(breakdown?.sourceEndOffset)) ? Number(breakdown.sourceEndOffset) : -1
    if (start < 0 || end <= start || end > text.length || start < cursor) continue

    if (start > cursor) {
      segments.push({
        key: `${keyPrefix}-plain-${cursor}-${start}`,
        text: text.slice(cursor, start),
        breakdown: null,
      })
    }

    segments.push({
      key: `${keyPrefix}-breakdown-${breakdown.id}`,
      text: text.slice(start, end),
      breakdown,
    })
    cursor = end
  }

  if (cursor < text.length) {
    segments.push({
      key: `${keyPrefix}-plain-${cursor}-${text.length}`,
      text: text.slice(cursor),
      breakdown: null,
    })
  }

  return segments
}

export function resolveTrimmedSelection(rawText, startOffset) {
  const normalizedText = normalizeSelectionText(rawText)
  const leadingWhitespace = normalizedText.match(/^\s*/u)?.[0]?.length || 0
  const trailingWhitespace = normalizedText.match(/\s*$/u)?.[0]?.length || 0
  const sourceStartOffset = startOffset + leadingWhitespace
  const sourceEndOffset = startOffset + normalizedText.length - trailingWhitespace
  return {
    sourceText: normalizedText.trim(),
    sourceStartOffset,
    sourceEndOffset,
  }
}

export function isSelectionOverlapping(breakdowns, startOffset, endOffset, excludeId = 0) {
  const list = Array.isArray(breakdowns) ? breakdowns : []
  return list.some((item) => {
    if (Number(item?.id || 0) === Number(excludeId || 0)) return false
    const currentStart = Number(item?.sourceStartOffset || 0)
    const currentEnd = Number(item?.sourceEndOffset || 0)
    return currentStart < endOffset && currentEnd > startOffset
  })
}

function escapeSvgText(value) {
  return String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    '\'': '&#39;',
  }[char] || char))
}
